import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import { G_PREFIX } from "../constants";
import { UpdateHostClassService } from "../services/update-host-class.service";

import { GButtonType, GSizeType, GType } from "../types";
import { colorIsLight } from "../utils/color";

@Component({
  selector: "g-button",
  template: `<button
    #el
    class="g-button"
    matRipple
    [disabled]="this.disabled"
    (click)="onClick.emit($event)"
  >
    <ng-content></ng-content>
  </button>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [`.g-menu-item{display:flex;position:relative;align-items:center;justify-content:flex-start;overflow:hidden;padding:0 16px;-webkit-user-select:none;-moz-user-select:none;-ms-user-select:none;user-select:none;outline:0;border:none;-webkit-tap-highlight-color:transparent;cursor:pointer;width:100%;text-align:left;box-sizing:border-box;color:inherit;font-size:inherit;background:0 0;text-decoration:none;margin:0;min-height:48px}.g-menu-item:hover:not([disabled]){background:rgba(0,0,0,.04)}.g-menu-item[disabled]{cursor:default;opacity:.38}.g-placeholder{display:inline-block;min-height:1em;vertical-align:middle;cursor:wait;width:100%;background-color:var(--color-grey-500);opacity:.5}.g-placeholder.btn::before{display:inline-block;content:""}.g-placeholder-small{min-height:16px}.g-placeholder-standard{min-height:24px}.g-placeholder-glow .g-placeholder{-webkit-animation:2s ease-in-out infinite placeholder-glow;animation:2s ease-in-out infinite placeholder-glow}@-webkit-keyframes placeholder-glow{50%{opacity:.2}}@keyframes placeholder-glow{50%{opacity:.2}}.g-placeholder-wave{-webkit-mask-image:linear-gradient(130deg,#000 25%,rgba(0,0,0,.8) 75%,#000 55%);mask-image:linear-gradient(130deg,#000 25%,rgba(0,0,0,.8) 75%,#000 55%);-webkit-mask-size:200% 100%;mask-size:200% 100%;-webkit-animation:2s linear infinite placeholder-wave;animation:2s linear infinite placeholder-wave}@-webkit-keyframes placeholder-wave{100%{-webkit-mask-position:-200% 0;mask-position:-200% 0}}@keyframes placeholder-wave{100%{-webkit-mask-position:-200% 0;mask-position:-200% 0}}g-button{display:inline-flex}.g-button{border-radius:100px;border-width:0;cursor:pointer;transition:.4s;display:inline-flex;align-items:center}.g-button.g--default .mat-ripple-element{background-color:#000;opacity:.1}.g-button.g--primary{--theme-color:var(--color-primary);--theme-text-color:#fff}.g-button.g--secondary{--theme-color:var(--color-secondary);--theme-text-color:#fff}.g-button.g--ca{--theme-color:#1c4c49;--theme-text-color:#fff}.g-button.g--disabled{--theme-color:#f3f4f6!important;--theme-text-color:#b4bac4!important}.g-button .mat-ripple-element{background-color:var(--theme-color);opacity:.1}.g-button.filled .mat-ripple-element{background-color:#fff!important;opacity:.1}.g-button .g-button-icon{display:inline-flex;margin-left:6px}.g-button--standard{padding:12px 24px;line-height:1.4}.g-button--standard .g-button-icon{max-height:24px;font-size:24px;width:24px;text-align:center}.g-button--small{padding:10px 16px;line-height:1.4}.g-button--small .g-button-icon,.g-button--small mat-icon{max-height:16px;font-size:16px;width:16px;text-align:center}.g-button--basic,.g-button--stroked{background:0 0;color:var(--theme-color)}.g-button--basic:hover:before,.g-button--stroked:hover:before{content:"";position:absolute;top:50%;left:50%;-webkit-transform:translate(-50%,-50%);transform:translate(-50%,-50%);width:100%;height:100%;background:var(--color-grey-500);opacity:.02;z-index:1}.g-button--flat,.g-button--raised{background:var(--theme-color,var(--color-white));color:var(--theme-text-color,var(--color-grey-800))}.g-button--raised{box-shadow:1px 1px 2px rgba(0,0,0,.12)}.g-button--stroked{border-width:2px;border-color:rgba(0,0,0,.12)}.g-button--icon{display:inline-flex;align-items:center;justify-content:center;padding:5px}.g-button--icon.g-button--small{width:36px;height:36px}.g-button--icon.g-button--standard{width:48px;height:48px}`],
  providers: [UpdateHostClassService],
  host: {},
  encapsulation: ViewEncapsulation.None,
})
export class ButtonComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() color: GType | string = "default";
  @Input() size: GSizeType = "standard";
  @Input() type: GButtonType = "basic";
  @Input() disabled: boolean;
  @Input() disableRipple: boolean;
  @Input() icon: boolean;
  @Input() textColor: string;
  // tslint:disable-next-line:no-output-on-prefix
  @Output() onClick: EventEmitter<any> = new EventEmitter();
  @ViewChild("el", { static: true }) el: ElementRef;
  constructor(
    private _updateHostClassService: UpdateHostClassService,
    private _renderer: Renderer2
  ) {}

  ngOnInit() {}

  private updateClass(): void {
    if (this.color.indexOf("#") > -1) {
      this.updatePropertyColor(this.color, this.textColor);
    }
    const prefixCls = G_PREFIX + "-button";
    this._updateHostClassService.updateHostClass(this.el.nativeElement, {
      [`${G_PREFIX}--${this.color}`]: this.color,
      [`${prefixCls}--${this.size}`]: this.size,
      [`${prefixCls}--icon`]: this.icon,
      [`${prefixCls}--${this.type}`]: this.type,
      [`${G_PREFIX}--disabled`]: this.disabled,
      filled:
        (this.type === "raised" ||
          this.type === "flat" ||
          this.type === "icon") &&
        this.color !== "default",
    });
  }

  private updatePropertyColor(color: string, textColor?: string) {
    this._renderer.setAttribute(
      this.el.nativeElement,
      "style",
      `--theme-color:${color};
      --theme-text-color: ${
        textColor ? textColor : colorIsLight(color) ? "#000000" : "#ffffff"
      }`
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { color, type, size, disabled } = changes;
    if (color || type || size || disabled) {
      this.updateClass();
    }
  }

  ngAfterViewInit(): void {
    this.updateClass();
  }
}
